import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { BillingPaymentSourceSummary } from '@dreamai/types';
import type { Billing } from '@airwallex/components-sdk';
import { ReactComponent as CardAgentIcon } from '../../../icons/card-agent.svg';
import { ReactComponent as CardAmexIcon } from '../../../icons/card-amex.svg';
import { ReactComponent as CardMastercardIcon } from '../../../icons/card-mastercard.svg';
import { ReactComponent as CardVisaIcon } from '../../../icons/card-visa.svg';
import { ReactComponent as CardOtherIcon } from '../../../icons/card-other.svg';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { CreditCardPlusIcon } from './transactional-icons';
import { getAirwallexBillingSdk } from './addFundsAirwallex';
import './add-funds-tray.scss';

// Spring (response 0.45s, damping 0.8), like iOS sheets.
export const TRAY_MS = 575;
const TRAY_EASE = `linear(
  0, 0.018, 0.065, 0.131, 0.209, 0.292, 0.377, 0.46, 0.539, 0.612, 0.678,
  0.737, 0.789, 0.834, 0.872, 0.904, 0.931, 0.953, 0.97, 0.984, 0.994, 1.002,
  1.007, 1.011, 1.014, 1.015, 1.015, 1.015, 1.014, 1.013, 1.012, 1.011, 1.01,
  1.008, 1.007, 1.006, 1.005, 1.004, 1.003, 1.003, 1
)`;
const LABEL_MS = 200;
// Airwallex's "ready" fires before its card fields finish growing, so wait
// until none of its frames has changed height for this long.
const AIRWALLEX_SETTLE_MS = 300;
const AIRWALLEX_SETTLE_MAX_MS = 8000;
const AIRWALLEX_MOUNT_ID = 'add-funds-airwallex-form';
export const NEW_CARD = 'new';

export const brandLabel = (brand: string | null): string => {
  const b = (brand ?? '').toLowerCase();
  if (b.includes('amex') || b.includes('american')) return 'Amex';
  if (b.includes('master')) return 'Mastercard';
  if (b.includes('visa')) return 'Visa';
  if (!b) return 'Card';
  return b.charAt(0).toUpperCase() + b.slice(1);
};

const walletLabel = (wallet: BillingPaymentSourceSummary['wallet']) =>
  wallet === 'apple_pay'
    ? 'Apple Pay'
    : wallet === 'google_pay'
      ? 'Google Pay'
      : null;

const BrandIcon: React.FC<{ brand: string | null }> = ({ brand }) => {
  const label = brandLabel(brand);
  if (label === 'Amex') return <CardAmexIcon />;
  if (label === 'Mastercard') return <CardMastercardIcon />;
  if (label === 'Visa') return <CardVisaIcon />;
  return <CardOtherIcon />;
};

/** Primary button whose label cross-fades and whose width eases to fit a new label. */
export const MorphButton = ({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: string }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const last = useRef<{ label: string; width: number } | null>(null);
  const [outgoing, setOutgoing] = useState<string | null>(null);

  useLayoutEffect(() => {
    const btn = ref.current;
    if (!btn || typeof btn.animate !== 'function') return;
    const width = btn.offsetWidth;
    const prev = last.current;
    last.current = { label: children, width };
    if (!prev || prev.label === children) return;
    const timing = { duration: LABEL_MS, easing: 'ease' };
    if (Math.abs(prev.width - width) > 1) {
      btn.animate?.(
        [{ width: `${prev.width}px` }, { width: `${width}px` }],
        timing,
      );
    }
    labelRef.current?.animate?.(
      [{ opacity: 0 }, { opacity: 0, offset: 0.4 }, { opacity: 1 }],
      timing,
    );
    setOutgoing(prev.label);
    const t = window.setTimeout(() => setOutgoing(null), LABEL_MS);
    return () => window.clearTimeout(t);
  }, [children]);

  return (
    <button ref={ref} className={`${className} csd-morph`} {...props}>
      <span ref={labelRef}>{children}</span>
      {outgoing && (
        <span key={outgoing} className='csd-morph-out' aria-hidden='true'>
          {outgoing}
        </span>
      )}
    </button>
  );
};

// The tray grows out of the footer: the white chin animates its height while
// the content (laid out at full size) is clipped to the chin's top edge.
const animateTray = (
  chin: HTMLElement,
  inner: HTMLElement,
  from: number,
  to: number,
  natural: number,
  duration: number,
  fill: FillMode = 'none',
) => {
  if (typeof chin.animate !== 'function') return null;
  const timing = { duration, easing: TRAY_EASE, fill };
  const clip = (h: number) => `inset(${Math.max(natural - h, 0)}px 0 0 0)`;
  inner.animate([{ clipPath: clip(from) }, { clipPath: clip(to) }], timing);
  return chin.animate([{ height: `${from}px` }, { height: `${to}px` }], timing);
};

export interface CardSetupSession {
  clientSecret: string;
  env: 'demo' | 'prod';
}

/**
 * Airwallex's embedded Billing checkout form (SETUP mode). The card is saved
 * by Airwallex; nothing card-shaped passes through Dream.
 */
export const AirwallexCardForm: React.FC<{
  startSetup: () => Promise<CardSetupSession>;
  onSaved: () => void;
  onSettled: () => void;
}> = ({ startSetup, onSaved, onSettled }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const onSavedRef = useRef(onSaved);
  const onSettledRef = useRef(onSettled);
  const startSetupRef = useRef(startSetup);
  onSavedRef.current = onSaved;
  onSettledRef.current = onSettled;
  startSetupRef.current = startSetup;

  useEffect(() => {
    let cancelled = false;
    let element: { unmount: () => void } | null = null;
    let watch: number | undefined;
    const watchUntilSettled = () => {
      const start = performance.now();
      let last = '';
      let stableSince = start;
      watch = window.setInterval(() => {
        const now = performance.now();
        const heights = Array.from(
          mountRef.current?.querySelectorAll('iframe') ?? [],
          (f) => f.offsetHeight,
        ).join('/');
        if (heights !== last) {
          last = heights;
          stableSince = now;
          return;
        }
        if (
          now - stableSince >= AIRWALLEX_SETTLE_MS ||
          now - start >= AIRWALLEX_SETTLE_MAX_MS
        ) {
          window.clearInterval(watch);
          onSettledRef.current();
        }
      }, 100);
    };
    (async () => {
      try {
        const session = await startSetupRef.current();
        const { billing } = await getAirwallexBillingSdk(session.env);
        if (!billing) throw new Error('Airwallex billing did not load');
        const checkout = await billing.createCheckout({
          client_secret: session.clientSecret,
        });
        // The SDK types the element name as an ambient const enum, which
        // isolatedModules cannot reference at runtime.
        const paymentForm = await checkout.createElement(
          'paymentForm' as Billing.CheckoutElementName,
        );
        if (cancelled || !mountRef.current) return;
        paymentForm.on('ready', watchUntilSettled);
        paymentForm.on('success', () => onSavedRef.current());
        paymentForm.on('error', (e: { message?: string } | undefined) => {
          setError(e?.message || 'Airwallex could not save this card.');
          onSettledRef.current();
        });
        paymentForm.mount(AIRWALLEX_MOUNT_ID);
        element = paymentForm;
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          onSettledRef.current();
        }
      }
    })();
    return () => {
      cancelled = true;
      window.clearInterval(watch);
      element?.unmount();
    };
  }, []);

  return (
    <>
      {error && <p className='spend-sheet-error'>{error}</p>}
      <div ref={mountRef} id={AIRWALLEX_MOUNT_ID} className='csd-airwallex' />
    </>
  );
};

export const CardSelect: React.FC<{
  cards: BillingPaymentSourceSummary[];
  value: string;
  onChange: (value: string) => void;
}> = ({ cards, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const selected = cards.find((c) => c.id === value);
  const row = (card: BillingPaymentSourceSummary | null) =>
    card ? (
      <>
        <BrandIcon brand={card.brand} />
        <span>
          <span className='csd-card-type'>
            {walletLabel(card.wallet) ?? brandLabel(card.brand)}
          </span>{' '}
          {card.last4 && (
            <>
              <span className='csd-dots'>••••</span> {card.last4}
            </>
          )}
        </span>
      </>
    ) : (
      <>
        <CreditCardPlusIcon className='csd-add-card-icon' />
        <span className='csd-add-card-label'>Add a new card</span>
      </>
    );

  return (
    <div className={`csd-select ${open ? 'csd-select--open' : ''}`}>
      <button
        type='button'
        className='csd-select-trigger'
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className='csd-select-value'>{row(selected ?? null)}</span>
        <ChevronDownIcon className='csd-select-chevron' />
      </button>
      {open && (
        <ul className='csd-select-list' role='listbox'>
          {[...cards.map((c) => c.id), NEW_CARD].map((id) => (
            <li key={id}>
              <button
                type='button'
                role='option'
                aria-selected={id === value}
                className='csd-select-option'
                onClick={() => {
                  onChange(id);
                  setOpen(false);
                }}
              >
                {row(cards.find((c) => c.id === id) ?? null)}
                {id === value && (
                  <span className='card-check card-check--on'>
                    <CheckIcon />
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export type AddFundsStep = 'pick' | 'add-card' | 'topping';

export interface AddFundsTrayProps {
  /** Shown while open; the tray animates closed when this turns false. */
  open: boolean;
  /** The footer the tray grows out of (its height is the start of the animation). */
  footerRef: React.RefObject<HTMLElement | null>;
  cardName: string;
  /** Formatted amount that will be added (limit − balance), e.g. "£448.00". */
  amountLabel: string;
  cards: BillingPaymentSourceSummary[];
  cardsLoading: boolean;
  selectedCardId: string;
  onSelectCard: (id: string) => void;
  step: AddFundsStep;
  onStepChange: (step: AddFundsStep) => void;
  startCardSetup: () => Promise<CardSetupSession>;
  /** The embedded form saved a card; refresh the list and select the new one. */
  onCardSaved: () => Promise<void>;
  /** Charge the selected card and confirm the spend. */
  onAddFunds: () => void;
  onCancel: () => void;
  errorMessage?: string | null;
}

/**
 * The "Add funds" tray that grows out of the spend sheet's footer when the
 * Assistant Card needs topping up: pick which saved card pays, or add one
 * through Airwallex's embedded form, then one click adds the funds and
 * confirms the spend.
 */
const AddFundsTray: React.FC<AddFundsTrayProps> = ({
  open,
  footerRef,
  cardName,
  amountLabel,
  cards,
  cardsLoading,
  selectedCardId,
  onSelectCard,
  step,
  onStepChange,
  startCardSetup,
  onCardSaved,
  onAddFunds,
  onCancel,
  errorMessage,
}) => {
  const trayRef = useRef<HTMLDivElement>(null);
  const chinRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const trayHeightRef = useRef(0);
  const addCardBodyRef = useRef<HTMLDivElement>(null);
  const [formReady, setFormReady] = useState(false);
  // Keeps the last step on screen while the tray animates closed.
  const [shownStep, setShownStep] = useState<AddFundsStep | null>(null);
  const shown = open ? step : shownStep;

  useEffect(() => {
    if (open) setShownStep(step);
  }, [open, step]);

  useLayoutEffect(() => {
    const tray = trayRef.current;
    const chin = chinRef.current;
    const inner = innerRef.current;
    const footer = footerRef.current;
    if (!tray || !chin || !inner || !footer) return;
    const natural = tray.offsetHeight;
    if (open && trayHeightRef.current === 0) {
      if (typeof chin.getAnimations === 'function') {
        [chin, inner].forEach((el) =>
          el.getAnimations().forEach((a) => a.cancel()),
        );
      }
      trayHeightRef.current = natural;
      animateTray(chin, inner, footer.offsetHeight, natural, natural, TRAY_MS);
      inner.animate?.(
        [{ opacity: 0 }, { opacity: 0, offset: 0.4 }, { opacity: 1 }],
        { duration: TRAY_MS, easing: 'ease-out' },
      );
    } else if (!open && trayHeightRef.current > 0) {
      trayHeightRef.current = 0;
      inner.animate?.(
        [{ opacity: 1 }, { opacity: 0, offset: 0.4 }, { opacity: 0 }],
        { duration: TRAY_MS, fill: 'forwards' },
      );
      const closing = animateTray(
        chin,
        inner,
        natural,
        footer.offsetHeight,
        natural,
        TRAY_MS,
        'forwards',
      );
      if (closing) {
        closing.onfinish = () => {
          if (trayHeightRef.current === 0) setShownStep(null);
        };
      } else {
        setShownStep(null);
      }
    }
  }, [open, footerRef]);

  // Smooth height changes inside the open tray (step changes, Airwallex form load).
  useEffect(() => {
    const tray = trayRef.current;
    const chin = chinRef.current;
    const inner = innerRef.current;
    if (!open || !tray || !chin || !inner) return;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const to = tray.offsetHeight;
      if (!trayHeightRef.current || Math.abs(to - trayHeightRef.current) < 2) {
        return;
      }
      const from =
        typeof chin.getAnimations === 'function' && chin.getAnimations().length
          ? chin.getBoundingClientRect().height
          : trayHeightRef.current;
      trayHeightRef.current = to;
      animateTray(chin, inner, from, to, to, 300);
    });
    observer.observe(inner);
    return () => observer.disconnect();
  }, [open]);

  // Airwallex focuses its card field while hidden, which scrolls the body
  // down; start the revealed form from the top.
  useLayoutEffect(() => {
    if (!formReady) return;
    const toTop = () => addCardBodyRef.current?.scrollTo?.({ top: 0 });
    toTop();
    const frame = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(frame);
  }, [formReady]);

  const addingCard = selectedCardId === NEW_CARD || cards.length === 0;
  const primaryLabel = addingCard
    ? cards.length === 0
      ? 'Add a payment card'
      : 'Add a new card'
    : `Add ${amountLabel} and confirm spend`;

  const handlePrimary = useCallback(() => {
    if (addingCard) {
      setFormReady(false);
      onStepChange('add-card');
    } else {
      onAddFunds();
    }
  }, [addingCard, onAddFunds, onStepChange]);

  const body = (s: AddFundsStep) => {
    if (s === 'topping') {
      return (
        <div
          className='csd-lower-body csd-lower-body--center'
          aria-live='polite'
        >
          <CardAgentIcon className='csd-lower-art' />
          <h2>Adding funds</h2>
          <p>Please wait, we&rsquo;re adding funds to {cardName}</p>
          <span className='csd-spinner' aria-hidden='true' />
        </div>
      );
    }
    if (s === 'add-card') {
      return (
        <>
          <div
            ref={addCardBodyRef}
            onScroll={(e) => {
              if (!formReady) e.currentTarget.scrollTop = 0;
            }}
            className={`csd-lower-body csd-add-card ${
              formReady ? 'csd-add-card--ready' : ''
            }`}
          >
            <CardAgentIcon className='csd-lower-art' />
            <h2 key={`title-${formReady}`} className='csd-add-card-swap'>
              {formReady ? 'Add a payment card' : 'Getting things ready...'}
            </h2>
            <p
              key={`body-${formReady}`}
              className='csd-add-card-swap'
              aria-live='polite'
            >
              {formReady
                ? 'Enter your card details below.'
                : 'This only takes a few seconds.'}
            </p>
            {!formReady && (
              <div className='csd-add-card-ghost' aria-hidden='true'>
                <div className='csd-add-card-ghost-wallets'>
                  <span />
                  <span />
                </div>
                <img
                  className='csd-add-card-ghost-logo'
                  src='/dream-loading.svg'
                  alt=''
                  width='72'
                  height='72'
                />
              </div>
            )}
            <AirwallexCardForm
              startSetup={startCardSetup}
              onSettled={() => setFormReady(true)}
              onSaved={async () => {
                await onCardSaved();
                onStepChange('pick');
              }}
            />
          </div>
          <div className='csd-lower-foot'>
            <button
              type='button'
              className='tx-btn-text'
              onClick={() => onStepChange('pick')}
            >
              Back
            </button>
          </div>
        </>
      );
    }
    return (
      <>
        <div className='csd-lower-body csd-lower-body--topup'>
          <CardAgentIcon className='csd-lower-art' />
          <h2>Add funds to {cardName}</h2>
          <p>
            <strong>{amountLabel}</strong> will be added to {cardName} to cover
            this purchase. Choose which card to use.
          </p>
          {cards.length === 0 ? (
            <div className='csd-select-trigger csd-select-trigger--static'>
              <span className='csd-select-value'>
                <CreditCardPlusIcon className='csd-add-card-icon' />
                <span>
                  {cardsLoading
                    ? 'Loading your cards…'
                    : 'You need to add a payment card'}
                </span>
              </span>
            </div>
          ) : (
            <CardSelect
              cards={cards}
              value={selectedCardId}
              onChange={onSelectCard}
            />
          )}
          {errorMessage && <p className='spend-sheet-error'>{errorMessage}</p>}
        </div>
        <div className='csd-lower-foot'>
          <button type='button' className='tx-btn-text' onClick={onCancel}>
            Cancel
          </button>
          <MorphButton
            type='button'
            className='tx-btn-primary'
            onClick={handlePrimary}
            disabled={cardsLoading && cards.length === 0}
          >
            {primaryLabel}
          </MorphButton>
        </div>
      </>
    );
  };

  if (!shown) return null;
  return (
    <div
      ref={trayRef}
      className={`csd-lower ${open ? '' : 'csd-lower--closing'}`}
      data-testid='add-funds-tray'
    >
      <div ref={chinRef} className='csd-lower-chin' aria-hidden='true' />
      <div ref={innerRef} className='csd-lower-inner'>
        {body(shown)}
      </div>
    </div>
  );
};

export default AddFundsTray;
